import React, { useState, useEffect } from 'react';
import { SavedAdventure } from '../types';
import { listAdventures, deleteAdventure } from '../services/api-client';
import { X, Trophy, Skull, Sparkles, Calendar, RotateCcw, Trash2, Play } from 'lucide-react';

interface SavedAdventuresProps {
  isDarkMode: boolean;
  onClose: () => void;
  onLoadAdventure: (adventure: SavedAdventure) => void;
}

export const SavedAdventures: React.FC<SavedAdventuresProps> = ({ isDarkMode, onClose, onLoadAdventure }) => {
  const [adventures, setAdventures] = useState<SavedAdventure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'in_progress' | 'completed'>('all');

  useEffect(() => {
    loadAdventures();
  }, [filter]);

  const loadAdventures = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listAdventures(filter === 'all' ? undefined : filter);
      setAdventures(result.adventures);
    } catch (err) {
      setError('Failed to load adventures');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this adventure?')) return;

    try {
      await deleteAdventure(id);
      setAdventures(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      setError('Failed to delete adventure');
      console.error(err);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getOutcomeIcon = (outcome?: string) => {
    if (outcome === 'win') return <Trophy size={16} className="text-yellow-500" />;
    if (outcome === 'alternative_win') return <Sparkles size={16} className="text-purple-500" />;
    if (outcome === 'lose') return <Skull size={16} className="text-red-500" />;
    return <Play size={16} className="text-blue-500" />;
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty === 1) return isDarkMode ? 'text-green-400' : 'text-green-600';
    if (difficulty === 2) return isDarkMode ? 'text-yellow-400' : 'text-yellow-600';
    if (difficulty === 3) return isDarkMode ? 'text-red-400' : 'text-red-600';
    return isDarkMode ? 'text-purple-400' : 'text-purple-600';
  };

  const getDifficultyLabel = (difficulty: number) => {
    if (difficulty === 1) return 'Easy';
    if (difficulty === 2) return 'Medium';
    if (difficulty === 3) return 'Hard';
    return 'Chaos';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className={`w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            My Adventures
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className={`flex gap-2 p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {(['all', 'in_progress', 'completed'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? isDarkMode
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-500 text-white'
                  : isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f === 'all' ? 'All' : f === 'in_progress' ? 'In Progress' : 'Completed'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Loading adventures...
            </div>
          ) : error ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              {error}
            </div>
          ) : adventures.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No adventures found. Start playing to save your progress!
            </div>
          ) : (
            <div className="space-y-3">
              {adventures.map((adventure) => (
                <button
                  key={adventure.id}
                  onClick={() => onLoadAdventure(adventure)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.01] active:scale-[0.99] ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 hover:border-blue-500'
                      : 'bg-gray-50 border-gray-200 hover:border-blue-400'
                  }`}
                >
                  {/* Top Row: Status Icon + Time */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getOutcomeIcon(adventure.gameOutcome || undefined)}
                      <span className={`text-xs font-medium ${
                        adventure.status === 'completed'
                          ? isDarkMode ? 'text-gray-300' : 'text-gray-700'
                          : isDarkMode ? 'text-blue-400' : 'text-blue-600'
                      }`}>
                        {adventure.status === 'completed' 
                          ? adventure.gameOutcome === 'win' 
                            ? 'Victory' 
                            : adventure.gameOutcome === 'alternative_win'
                            ? 'Alt Victory'
                            : 'Defeat'
                          : 'In Progress'
                        }
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {formatDate(adventure.timestamp)}
                      </span>
                      <button
                        onClick={(e) => handleDelete(adventure.id, e)}
                        className={`p-1 rounded transition-colors ${
                          isDarkMode
                            ? 'hover:bg-gray-600 text-gray-500 hover:text-red-400'
                            : 'hover:bg-gray-200 text-gray-400 hover:text-red-600'
                        }`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Preview Text */}
                  <p className={`text-sm mb-3 line-clamp-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {(() => {
                      // Find the last AI message for preview
                      const lastAIMessage = [...adventure.messages].reverse().find(m => m.sender === 'ai');
                      if (lastAIMessage) {
                        return lastAIMessage.text.length > 150 
                          ? lastAIMessage.text.substring(0, 150) + '...' 
                          : lastAIMessage.text;
                      }
                      return 'No story yet';
                    })()}
                  </p>

                  {/* Stats Row */}
                  <div className={`flex flex-wrap items-center gap-3 text-xs ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <span className={getDifficultyColor(adventure.difficulty)}>
                      {getDifficultyLabel(adventure.difficulty)}
                    </span>
                    <span>{adventure.gameMode === 'endless' ? '∞ Endless' : '🏆 Adventure'}</span>
                    <span>{adventure.turns} turns</span>
                    {adventure.rewindPenalties > 0 && (
                      <span className="flex items-center gap-1">
                        <RotateCcw size={10} />
                        {adventure.rewindPenalties}
                      </span>
                    )}
                    {adventure.isDailyChallenge && (
                      <span className="flex items-center gap-1">
                        <Calendar size={10} />
                        Daily
                      </span>
                    )}
                  </div>

                  {/* Outcome Reason (for completed games) */}
                  {adventure.status === 'completed' && adventure.outcomeReason && (
                    <p className={`text-xs mt-2 italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      "{adventure.outcomeReason}"
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
